import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_ARR, DBO_V } from '@op/utils/format';
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag } from 'tea-component';
import { useHashtagData } from '../hooks';
import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import SvgIcon from '@home/components/SvgIcon';
import useIntls from '@opm/hooks/useIntls';
import { goToDetailsPage } from '@opm/utils/history';
import MinChart from '../components/MinChart';
import BubbleCommon from '@op/components/BubbleCommon';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { transCountryText } from '@op/utils';
import CountryItem from '../components/CountryItem';
import './index.scss';
import ImageList from '@opm/components/ImageList';
import HashtagRightContent from '../components/HashtagRightContent';
import classnames from 'classnames';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { useScroll } from '@op/hooks/useScroll';
import {
  hashtagRankTitleTipsMap,
  hashtagTrendTitleTipsMap,
} from '@opm/components/BubbleCommon';

const { injectable } = Table.addons;

function getHashtagId(data: any) {
  if (data) {
    return (
      data.rank + data.hashtag + data.source + data.time_range + data.md5_uin
    );
  }
}

export interface IVideoItemData {
  video_id: string;
  video_image: string;
  [key: string]: any;
}
export interface IHashtagItem {
  hashtag: string;
  rank: number;
  source: string;
  time_range: string;
  top_video_list?: IVideoItemData[];
  // ... 其他字段
}

export default function HashtagTable({
  filter,
  translateCategory,
  timeRangeIsChange: _timeRangeIsChange,
  instanceId = 'default',
}: {
  filter: any;
  translateCategory: (value: string, isEn: boolean) => string;
  timeRangeIsChange: boolean;
  instanceId?: string;
}) {
  const { intl, isEn } = useIntls();
  const [selectedHashtag, setSelectedHashtag] = useState<IHashtagItem | null>(
    null,
  );
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [newlyListedOnly, setNewlyListedOnly] = useState(false);

  const amendedFilter = useMemo(() => {
    if (!filter?.start_time) return null;
    return { ...filter, newly_listed_only: newlyListedOnly };
  }, [filter, newlyListedOnly]);

  const {
    changeSortBy,
    data,
    total,
    pageInfo,
    changePageInfo,
    sortBy,
    loading,
    keyword,
    changeKeyword,
    download,
    viewLatestData,
  } = useHashtagData({
    filter: amendedFilter,
    translateCategory,
    instanceId,
    intl,
  });
 

  const { columns, noRankSort, isToday, isTiktokGaming } = useMemo(() => {
    const isToday = filter?.time_range === 'today';
    const isTiktokGaming = filter?.source === 'tiktok_gaming';
    let rankTitle: any = intl('Rank');
    const moreCountries = filter?.countries?.length > 1;
    const noRankSort = true; // filter?.time_range === 'today' || moreCountries;

    if (noRankSort) {
      rankTitle = (
        <div className="dbom-hashtag-title">
          {rankTitle}
          <span className="dbom-hashtag-title-tips dbom-sort-by">
            (
            {intl('by {name}', {
              name: intl(
                sortBy?.by === 'tweets_views' || isTiktokGaming
                  ? 'Views'
                  : 'Videos-2',
              ),
            })}
            )
          </span>
        </div>
      );
    } else {
      rankTitle = (
        <BubbleCommon
          showIcon={false}
          placement="top"
          tipsMap={hashtagRankTitleTipsMap}
        >
          <div className="dbom-hashtag-title">
            {rankTitle}
            <span className="dbom-hashtag-title-tips dbom-sort-by">
              ({intl('by {name}', { name: intl('Hot') })})
            </span>
          </div>
        </BubbleCommon>
      );
    }
    const list: Array<any> = [
      {
        key: 'rank',
        header: rankTitle,
        width: 170,
        fixed: 'left',
        render: (data: any) => {
          const isUp = data.rank_diff > 0;
          const updateRank = Math.abs(data.rank_diff);
          return (
            <div className="dbom-hashtag-rank">
              <p className={`dbom-rank dbom-rank0${data.rank}`}>{data.rank}</p>
              {data.rank_diff !== 0 && (
                <RingRatioBubble
                  oldTime={[data.previous_rank_date]}
                  newTime={[data.current_rank_date]}
                  oldValue={data.rank + data.rank_diff}
                  newValue={data.rank}
                  placement="left"
                >
                  <p className={`dbom-rank-trend ${isUp ? '--up' : '--down'}`}>
                    <SvgIcon
                      type={isUp ? 'green-up-new' : 'red-down-new'}
                      className="dbom-rank-trend-icon"
                    />
                    <span className="dbom-rank-trend-num">{updateRank}</span>
                  </p>
                </RingRatioBubble>
              )}
            </div>
          );
        },
      },
      {
        key: 'hashtag',
        fixed: 'left',
        header: (
          <div className="dbom-hashtag-title">
            {intl({ id: 'Hashtags' })}
            {!isToday && !isTiktokGaming && (
              <span className="dbom-hashtag-title-tips">
                ({intl('Click hashtag to view more details')})
              </span>
            )}
          </div>
        ),
        width: 350,
        render: (cvm: any) => {
          return (
            <div className="dbom-hashtag-table-hashtag">
              <div className="dbom-hashtag-table-hashtag-hd">
                <div
                  className={`dbom-hashtag-table-hashtag-title ${
                    isToday || isTiktokGaming ? '--dbom-no-go' : ''
                  }`}
                  onClick={() => {
                    if (isToday || isTiktokGaming) return;
                    DBOM_POINT.btn('ClickHashtag', cvm.hashtag);
                    goToDetailsPage({
                      filter,
                      extraFilter: { ...cvm },
                      navigateUrl: '/marketing/Hashtag/Details',
                    });
                  }}
                >
                  {cvm.hashtag}
                </div>
                {cvm.is_promoted === 1 && <Tag>{intl('Promoted')}</Tag>}
              </div>
              <div className="dbom-hashtag-table-hashtag-bd">
                {moreCountries && (
                  <BubbleCommon
                    placement="left"
                    showIcon={false}
                    tipsMap={undefined}
                    content={transCountryText(cvm.country)}
                  >
                    <CountryItem
                      iconStyle={{ width: 16 }}
                      country={cvm?.country}
                      showText={false}
                    ></CountryItem>
                  </BubbleCommon>
                )}
                {translateCategory(cvm.category, isEn)}
              </div>
            </div>
          );
        },
      },
      {
        key: 'tweets_posts',
        align: 'right',
        header: intl({ id: 'Videos-2' }),
        width: 190,
        render: (cvm: any) => {
          const ratio = DBO_V.qoq(cvm.tweets_posts, cvm.previous_tweets_posts);
          return (
            <div className="dbom-hashtag-metric-cell">
              <span>{DBO_V.reviews(cvm.tweets_posts)}</span>
              {!newlyListedOnly && (
                <RingRatioBubble
                  oldTime={[cvm.previous_rank_date]}
                  newTime={[cvm.current_rank_date]}
                  oldValue={cvm.previous_tweets_posts}
                  newValue={cvm.tweets_posts}
                  formateValue={DBO_V.reviews}
                >
                  <PercentageItem percentage={ratio || 0} />
                </RingRatioBubble>
              )}
            </div>
          );
        },
      },
      {
        key: 'tweets_views',
        align: 'right',
        header: intl({ id: 'Views' }),
        width: 190,
        render: (cvm: any) => {
          const ratio = DBO_V.qoq(cvm.tweets_views, cvm.previous_tweets_views);
          return (
            <div className="dbom-hashtag-metric-cell">
              <span>{DBO_V.reviews(cvm.tweets_views)}</span>
              {!newlyListedOnly && (
                <RingRatioBubble
                  oldTime={[cvm.previous_rank_date]}
                  newTime={[cvm.current_rank_date]}
                  oldValue={cvm.previous_tweets_views}
                  newValue={cvm.tweets_views}
                  formateValue={DBO_V.reviews}
                >
                  <PercentageItem percentage={ratio || 0} />
                </RingRatioBubble>
              )}
            </div>
          );
        },
      },
    ];

    // tiktok_gaming 时过滤掉 tweets_posts 列
    if (isTiktokGaming) {
      const tweetsPostsIndex = list.findIndex(
        (item) => item.key === 'tweets_posts',
      );
      if (tweetsPostsIndex > -1) {
        list.splice(tweetsPostsIndex, 1);
      }
    }

    if (!isToday) {
      list.push({
        key: 'trend',
        header: (
          <BubbleCommon placement="top" tipsMap={hashtagTrendTitleTipsMap}>
            {intl({ id: 'Trend' })}
          </BubbleCommon>
        ),
        width: 200,
        render: (cvm: any) => {
          const timeline = cvm.trend || []; 
          return (
            <div>
              <MinChart timeline={timeline}></MinChart>
            </div>
          );
        },
      });
    }

    if (!isToday) {
      list.push({
        key: 'video',
        width: 150,
        header: intl({ id: 'Video' }),
        render: (cvm: any) => {
          const { top_video_list: video } = cvm;
          if (!video) {
            return null;
          }
          return (
            <ImageList
              list={video}
              showPlayIcon
              onClick={() => handleVideoClick(cvm)}
            />
          );
        },
      });
    }

    return { columns: list, noRankSort, isToday, isTiktokGaming };
  }, [filter, intl, sortBy, newlyListedOnly]);

  // 获取排序列配置
  const getSortColumns = (noRankSort: boolean, isTiktokGaming: boolean) => {
    if (noRankSort) {
      return isTiktokGaming
        ? ['tweets_views']
        : ['tweets_posts', 'tweets_views'];
    }
    return isTiktokGaming
      ? ['rank', 'tweets_views']
      : ['rank', 'tweets_posts', 'tweets_views'];
  };

  // 获取排序选项配置
  const sortOptions = useMemo(() => {
    const baseOptions: Record<string, { text: string; value: string }> = {
      hot: { text: 'Hot', value: 'rank' },
      videos: { text: 'Videos-2', value: 'tweets_posts' },
      views: { text: 'Views', value: 'tweets_views' },
    };

    if (noRankSort) {
      return isTiktokGaming
        ? [baseOptions.views]
        : [baseOptions.videos, baseOptions.views];
    }
    return isTiktokGaming
      ? [baseOptions.hot, baseOptions.views]
      : [baseOptions.hot, baseOptions.videos, baseOptions.views];
  }, [noRankSort, isTiktokGaming]);

  const { eleRef: tableRef, scroll: scrollTableInfo } =
    useScroll<HTMLTableElement>({
      classname: '.tea-table__body',
      value: 1350,
      scrollDirection: 'scrollLeft',
    });

  const { eleRef: divRef, scroll: scrollDivInfo } = useScroll<HTMLDivElement>({
    value: 0,
    scrollDirection: 'scrollTop',
  });

  const handleVideoClick = (hashtag: any) => {
    setSelectedHashtag(hashtag);
    setShowVideoPlayer(true);
    scrollDivInfo();
    setTimeout(() => {
      scrollTableInfo();
    }, 1000);
  };

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
    setSelectedHashtag(null);
  };

  // data变后，关闭视频播放器
  useEffect(() => {
    handleCloseVideoPlayer();
  }, [data]);

  // tiktok_gaming 时，如果当前排序是 tweets_posts，切换为 tweets_views
  useEffect(() => {
    if (isTiktokGaming && sortBy?.by === 'tweets_posts') {
      changeSortBy({ by: 'tweets_views', order: 'desc' });
    }
  }, [isTiktokGaming, sortBy?.by]);

  const emptyInfo = useMemo(() => {
    if (
      ['last_7_days', 'last_30_days'].includes(filter?.time_range) &&
      !filter?.categories?.includes('all categories')
    ) {
      return {
        emptyDescText: (
          <>
            {intl(
              {
                id: 'TikTok platform has not provided relevant data for the selected date, {button}.',
              },
              {
                button: (
                  <span
                    className="dbom-hashtag-empty-desc-text__button"
                    onClick={() => viewLatestData()}
                  >
                    {intl({ id: 'click to view the latest data' })}
                  </span>
                ),
              },
            )}
          </>
        ),
      };
    }
    return {
      emptyDescText: (
        <>
          {intl({ id: 'No data under current filter selections' })}
          {', '}
          <span
            className="dbom-hashtag-empty-desc-text__button"
            onClick={() => viewLatestData()}
          >
            {intl({ id: 'click to view the latest data.' })}
          </span>
        </>
      ),
    };
  }, [filter, intl, viewLatestData]);

  return (
    <div className="dbom-hashtag-table-wrapper">
      <div
        className={classnames('dbom-hashtag-table-left', {
          '  --with-player': showVideoPlayer,
        })}
      >
        <CardCommon className="dbom-hashtag-table-cardCommon">
          <FilterBoxCommon
            left={[
              {
                key: 'sortBy',
                defaultValue: sortBy?.by,
                defaultParams: {
                  options: sortOptions,
                  onChange: (value: string) => {
                    changeSortBy({ by: value, order: 'desc' });
                  },
                },
              },
              {
                key: 'singleCheckbox',
                valueKey: 'newlyListedOnly',
                defaultValue: newlyListedOnly,
                defaultParams: {
                  text: isToday
                    ? 'View only first-time Top 200 hashtags'
                    : 'View only first-time Top 100 hashtags',
                },
              },
            ]}
            right={[
              {
                key: 'search',
                defaultValue: keyword,
                defaultParams: {
                  placeholder: intl({ id: 'Search Hashtag' }),
                  onSearch: (value: string) => {
                    changeKeyword(value);
                  },
                  onClear: () => {
                    changeKeyword('');
                  },
                },
              },
              {
                key: 'download',
                style: { marginLeft: 10 },
                event: {
                  onClick: () => download(),
                },
              },
            ]}
            onChange={(value, key) => {
              if (key === 'newlyListedOnly') {
                setNewlyListedOnly(value.newlyListedOnly);
              }
            }}
          ></FilterBoxCommon>
          <div>
            <TableCommonHook
              className="dbom-hashtag-table"
              sortInfo={[sortBy!, changeSortBy]}
              records={data}
              total={total}
              tableRef={tableRef as any}
              pageInfo={pageInfo}
              setPageInfo={changePageInfo}
              sortColumns={getSortColumns(noRankSort, isTiktokGaming)}
              recordKey={(data) => getHashtagId(data)}
              columns={columns}
              loading={loading}
              minWidth={1100}
              tableSticky
              emptyProps={emptyInfo}
              addons={[
                // 支持 props 注入
                injectable({
                  row: (props, context) => {
                    const isSelected =
                      getHashtagId(context.record) ===
                      getHashtagId(selectedHashtag);
                    return {
                      className: isSelected ? 'dbom-selected-table-row' : '',
                    };
                  },
                }),
              ]}
            ></TableCommonHook>
          </div>
        </CardCommon>
      </div>

      <div
        ref={divRef}
        className={classnames('dbom-hashtag-table-right', {
          ' --show': showVideoPlayer,
        })}
      >
        {selectedHashtag && (
          <HashtagRightContent
            hashtagInfo={selectedHashtag}
            filter={filter}
            onClose={handleCloseVideoPlayer}
          />
        )}
      </div>
    </div>
  );
}
